import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { AuxiliaryEquipmentService } from '../../../service/api/auxiliary-equipment-service';
import { redux } from 'Core/redux';

export const STORE_KEY = 'auxiliaryEquipment';
export const INITIAL_STATE = {
  data: [],
  loading: false,
  error: null,
  maintenanceRecordsData: [],
  maintenanceRecordData: null,
  maintenanceData: null,
  cellHistoryData: [],
  tileView: true,
};

// Fetch all auxiliary equipment
export const fetchAuxiliaryEquipments = createAsyncThunk(
  'auxiliaryEquipment/fetchAuxiliaryEquipments',
  async () => {
    const { response } = await AuxiliaryEquipmentService.getAllAuxiliaryEquipments();
    return response;
  }
);

// Fetch auxiliary equipment with filters
export const fetchAuxiliaryEquipmentsWithFilters = createAsyncThunk(
  'auxiliaryEquipment/fetchAuxiliaryEquipmentsWithFilters',
  async (filters = {}) => {
    const { response } = await AuxiliaryEquipmentService.getAllAuxiliaryEquipments(filters);
    return response;
  }
);

// Save auxiliary equipment
export const saveAuxiliaryEquipment = createAsyncThunk(
  'auxiliaryEquipment/saveAuxiliaryEquipment',
  async (data) => {
    const { response } = await AuxiliaryEquipmentService.saveAuxiliaryEquipment(data);
    return response;
  }
);

// Update auxiliary equipment
export const updateAuxiliaryEquipment = createAsyncThunk(
  'auxiliaryEquipment/updateAuxiliaryEquipment',
  async (data) => {
    const { response } = await AuxiliaryEquipmentService.updateAuxiliaryEquipment(data);
    return response;
  }
);

// Add auxiliary equipment (alias for save)
export const addAuxiliaryEquipment = createAsyncThunk(
  'auxiliaryEquipment/addAuxiliaryEquipment',
  async (data) => {
    const { response } = await AuxiliaryEquipmentService.saveNewAuxiliaryEquipment(data);
    return response;
  }
);

// Edit auxiliary equipment (alias for update)
export const editAuxiliaryEquipment = createAsyncThunk(
  'auxiliaryEquipment/editAuxiliaryEquipment',
  async (data) => {
    const { response } = await AuxiliaryEquipmentService.updateAuxiliaryEquipment(data);
    return response;
  }
);

// Delete auxiliary equipment
export const deleteAuxiliaryEquipment = createAsyncThunk(
  'auxiliaryEquipment/deleteAuxiliaryEquipment',
  async (id) => {
    const { response } = await AuxiliaryEquipmentService.deleteAuxiliaryEquipment(id);
    return response;
  }
);

// Save auxiliary equipment maintenance
export const saveAuxiliaryEquipmentMaintenance = createAsyncThunk(
  'auxiliaryEquipment/saveAuxiliaryEquipmentMaintenance',
  async (data) => {
    const { response } = await AuxiliaryEquipmentService.saveAuxiliaryEquipmentMaintenance(data);
    return response;
  }
);

// Fetch maintenance records by auxiliary equipment ID
export const fetchMaintenanceRecordsByAuxiliaryEquipmentId = createAsyncThunk(
  'auxiliaryEquipment/fetchMaintenanceRecordsByAuxiliaryEquipmentId',
  async (auxiliaryEquipmentId) => {
    const { response } = await AuxiliaryEquipmentService.getMaintenanceRecordsByAuxiliaryEquipmentId(auxiliaryEquipmentId);
    return response;
  }
);

// Fetch maintenance record details
export const fetchMaintenanceRecordsDetails = createAsyncThunk(
  'auxiliaryEquipment/fetchMaintenanceRecordsDetails',
  async (recordId) => {
    const { response } = await AuxiliaryEquipmentService.getMaintenanceRecordDetails(recordId);
    return response;
  }
);

// Add auxiliary equipment maintenance record
export const addAuxiliaryEquipmentMaintenanceRecord = createAsyncThunk(
  'auxiliaryEquipment/addAuxiliaryEquipmentMaintenanceRecord',
  async (data) => {
    const { response } = await AuxiliaryEquipmentService.addMaintenanceRecord(data);
    return response;
  }
);

// Fetch cell history by asset number
export const fetchAuxiliaryEquipmentByAssetNumber = createAsyncThunk(
  'auxiliaryEquipment/fetchByAssetNumber',
  async (assetNumber) => {
    const { response } = await AuxiliaryEquipmentService.getAllAuxiliaryByAssetNumber(assetNumber);
    return response;
  }
);

export const { reducer, actions } = createSlice({
  name: STORE_KEY,
  initialState: INITIAL_STATE,
  reducers: {
    setAuxiliaryEquipment: (state, action) => {
      // Ensure data is always an array
      if (Array.isArray(action.payload)) {
        state.data = action.payload;
      } else if (action.payload && typeof action.payload === 'object') {
        // If payload is a single object, wrap it in an array
        state.data = [action.payload];
      } else {
        state.data = [];
      }
    },
    setMaintenanceData: (state, action) => {
      state.maintenanceData = action.payload;
    },
    clearMaintenanceData: (state) => {
      state.maintenanceData = null;
    },
    resetFilters: (state) => {
      // Reset any filter-related state if needed
      state.loading = false;
      state.error = null;
    },
    setTileView: (state, action) => {
      state.tileView = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      // Fetch auxiliary equipment
      .addCase(fetchAuxiliaryEquipments.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAuxiliaryEquipments.fulfilled, (state, action) => {
        state.loading = false;
        // Ensure data is always an array
        if (Array.isArray(action.payload)) {
          state.data = action.payload;
        } else if (action.payload?.data && Array.isArray(action.payload.data)) {
          state.data = action.payload.data;
        } else if (action.payload && typeof action.payload === 'object') {
          // If payload is a single object, wrap it in an array
          state.data = [action.payload];
        } else {
          state.data = [];
        }
      })
      .addCase(fetchAuxiliaryEquipments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Fetch auxiliary equipment with filters
      .addCase(fetchAuxiliaryEquipmentsWithFilters.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAuxiliaryEquipmentsWithFilters.fulfilled, (state, action) => {
        state.loading = false;
        // Debug logging
        console.log('Auxiliary Equipment Redux - fetchAuxiliaryEquipmentsWithFilters.fulfilled:', {
          payload: action.payload,
          payloadType: typeof action.payload,
          isArray: Array.isArray(action.payload),
          payloadData: action.payload?.data
        });
        // Ensure data is always an array
        if (Array.isArray(action.payload)) {
          state.data = action.payload;
        } else if (action.payload?.data && Array.isArray(action.payload.data)) {
          state.data = action.payload.data;
        } else if (action.payload && typeof action.payload === 'object') {
          // If payload is a single object, wrap it in an array
          state.data = [action.payload];
        } else {
          state.data = [];
        }
        console.log('Auxiliary Equipment Redux - Final state.data:', state.data);
      })
      .addCase(fetchAuxiliaryEquipmentsWithFilters.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Save auxiliary equipment
      .addCase(saveAuxiliaryEquipment.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveAuxiliaryEquipment.fulfilled, (state, action) => {
        state.loading = false;
        state.data = [...state.data, action.payload];
      })
      .addCase(saveAuxiliaryEquipment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Update auxiliary equipment
      .addCase(updateAuxiliaryEquipment.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAuxiliaryEquipment.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.data.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.data[index] = action.payload;
        }
      })
      .addCase(updateAuxiliaryEquipment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Add auxiliary equipment
      .addCase(addAuxiliaryEquipment.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addAuxiliaryEquipment.fulfilled, (state, action) => {
        state.loading = false;
        state.data = [...state.data, action.payload];
      })
      .addCase(addAuxiliaryEquipment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Edit auxiliary equipment
      .addCase(editAuxiliaryEquipment.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editAuxiliaryEquipment.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.data.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.data[index] = action.payload;
        }
      })
      .addCase(editAuxiliaryEquipment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Delete auxiliary equipment
      .addCase(deleteAuxiliaryEquipment.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteAuxiliaryEquipment.fulfilled, (state, action) => {
        state.loading = false;
        state.data = state.data.filter(item => item.id !== action.payload);
      })
      .addCase(deleteAuxiliaryEquipment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Save auxiliary equipment maintenance
      .addCase(saveAuxiliaryEquipmentMaintenance.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveAuxiliaryEquipmentMaintenance.fulfilled, (state, action) => {
        state.loading = false;
        state.maintenanceData = action.payload;
      })
      .addCase(saveAuxiliaryEquipmentMaintenance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Fetch maintenance records by auxiliary equipment ID
      .addCase(fetchMaintenanceRecordsByAuxiliaryEquipmentId.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMaintenanceRecordsByAuxiliaryEquipmentId.fulfilled, (state, action) => {
        state.loading = false;
        state.maintenanceRecordsData = action.payload;
      })
      .addCase(fetchMaintenanceRecordsByAuxiliaryEquipmentId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Fetch maintenance record details
      .addCase(fetchMaintenanceRecordsDetails.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMaintenanceRecordsDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.maintenanceRecordData = action.payload;
      })
      .addCase(fetchMaintenanceRecordsDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Add auxiliary equipment maintenance record
      .addCase(addAuxiliaryEquipmentMaintenanceRecord.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addAuxiliaryEquipmentMaintenanceRecord.fulfilled, (state, action) => {
        state.loading = false;
        state.maintenanceRecordsData = [...state.maintenanceRecordsData, action.payload];
      })
      .addCase(addAuxiliaryEquipmentMaintenanceRecord.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Fetch cell history by asset number
      .addCase(fetchAuxiliaryEquipmentByAssetNumber.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAuxiliaryEquipmentByAssetNumber.fulfilled, (state, action) => {
        state.loading = false;
        state.cellHistoryData = action.payload;
      })
      .addCase(fetchAuxiliaryEquipmentByAssetNumber.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

redux.injectReducer(STORE_KEY, reducer);

// Export individual actions
export const { 
  setAuxiliaryEquipment, 
  setMaintenanceData, 
  clearMaintenanceData, 
  resetFilters, 
  setTileView 
} = actions;